import {Tailwind} from '@wandb/weave/components/Tailwind';
import {DocumentNode} from 'graphql';
import _ from 'lodash';
import React from 'react';
import {useLazyQuery} from 'react-apollo';
import {useParams} from 'react-router-dom';

import {useAccountTeamsList} from '../../pages/HomePage/HomePageSidebar/useAccountTeamsList';
import {useViewer} from '../../state/viewer/hooks';
import {useRampFlagAccountSelector} from '../../util/rampFeatureFlags';
import {useNavContext, useNavUpdaterContext} from '../NavContextProvider';
import {PageInfo} from './../../generated/graphql';
import {offsetCursor} from './utils';

export const sortDirections = {
  asc: '+',
  desc: '-',
} as const;
export type SortDirections = keyof typeof sortDirections;
export type SortOperators =
  (typeof sortDirections)[keyof typeof sortDirections];

export type PaginatedListProps<T, V> = {
  Components: {
    EmptyState: React.ComponentType<{
      isSearching: boolean;
      isCreatingProject: boolean;
      toggleCreateProjectDrawer: () => void;
      defaultTeam: string;
      isReadOnly: boolean;
    }>;
    ListWrapper: React.ComponentType<any>;
    Wrapper: React.ComponentType<any>;
    ListHeader: React.ComponentType<{
      sortOrder: [SortOperators | null, string];
      setSortOrder: (s: [SortOperators, string]) => void;
    }>;
    ListItem: React.ComponentType<{
      isLoading: boolean;
      itemData: V;
      personalEntity?: string;
      onDeleted?: () => void;
    }>;
    Meta: React.ComponentType<{
      fetchItems: (dir: 'prev' | 'next' | 'refetch') => void;
      hasNextPage: boolean;
      hasPreviousPage: boolean;
      listSize: number | undefined;
      searchPattern: string;
      setSearchPattern: (newPattern: string) => void;
    }>;
  };
  config: {
    extractPageInfo: (data: T | undefined) => PageInfo;
    extractListItems: (data: T) => V[];
    extractPersonalEntity?: (data: T) => string | undefined;
    listSize: number;
    orderBy: string;
    query: DocumentNode;
  };
  entityName?: string;
  orgID?: string;
  userID?: string;
  isReadOnly: boolean;
};

type PaginationArgs = {
  after: string;
  entityName: string;
  first: number;
  order: string | null;
  pattern: string;
  userName: string;
  orgID: string;
  userID: string;
  isReadOnly: boolean;
};

/**
 * This code attempts to do a few things:
 * - decouple the pagination logic from the query itself (so it can be used in many places)
 * - decouple the pagination logic from the view components (so it can handle many view concerns)
 * - (temporary) safely exploit the weird API behavior of encoding offset pagination as a base64 encoded "cursor" so that we can release this is advance of proper backend support without having to do something like client side data caching
 *
 * The interactions between the pieces are somewhat at a distance, but that's because of the decoupling. The intention here is that we can turn around and do the user projects lists almost immediately just by supplying a different query with different "extract" functions which handle getting the data and page info out of the query return with any arbitrary shape.
 *
 * The tracking of the query argument state through a mutable Ref might be something likely changes in the near future (although it would be invisible to the components generated out of these base level functions). At the time of development is helped solve some bugs where setting discrete bits of state thrashed the query
 */
export const PaginatedList = <QueryData, ListItem>({
  Components,
  config,
  entityName,
  orgID,
  userID,
  isReadOnly,
}: PaginatedListProps<QueryData, ListItem>) => {
  /**
   * Ordering is a combination of +/- and the order key. We use one state variable to avoid
   * double fetching when binding fetchMore to changes to both each triggering an effect
   */
  const [sortOrder, setSortOrder] = React.useState<
    [SortOperators | null, string]
  >([null, 'created_at']);

  /**
   * Search pattern is the text that's entered into the input
   */
  const [searchPattern, setSearchPattern] = React.useState('');

  /**
   * Note: this is sort of a weird pattern so there must be a reason. The basic problem is that pagination depends on a series of variables tracked in the UI (sort order, column, search pattern, page) and certain actions (such as clicking a new column to sort by) can change multiple values at once (sortBy, sortOrder) as well as affect other UI states (in this scenario we want to dump where a user is in the pagination and show either the top or bottom of the list). While developing, a constant battle was making reactive updates fetch the correct number of times (two updates not batched correctly would cause two renders). Batching is somewhat available with the update function syntax in a setState call, but in developing this I just ended up finding it way harder tracking the chain of events when (a) the updates are all tied to user actions (b) it's easy to know the combination of values that need to override the existing state at the time of fetch
   *
   * I think it may make sense to refactor this as a _single_ React state object that is ALWAYS updated via:
   * setState(prev => ({
   *   ...prev,
   *   ...fieldsToOverwrite
   * }))
   *
   * However I didn't think of this state batching approach until PR review time and I don't really want to delay this more days if I refactor this and end up hitting more issues with fetching dispatching incorrectly in response to React state updates.
   */
  const queryVars = React.useRef<PaginationArgs>({
    after: '',
    entityName: entityName ?? '',
    first: config.listSize,
    order: sortOrder[1],
    pattern: '',
    userName: entityName ?? '',
    orgID: orgID ?? '',
    userID: userID ?? '',
    isReadOnly,
  });

  // use a getter to keep functions from closing over current state of query vars
  // Note: this problem likely dissolves when moving state into React.setState({})
  function getQueryVars() {
    return queryVars.current;
  }

  const [fetchMore, {called, data, loading}] = useLazyQuery<QueryData>(
    config.query,
    {
      fetchPolicy: 'no-cache',
    }
  );
  const {isCreatingProject} = useNavContext();
  const {toggleCreateProjectDrawer} = useNavUpdaterContext();
  const {accountTeams} = useAccountTeamsList();
  const enableAccountSelector = useRampFlagAccountSelector();
  const {entityName: profilePageEntity} = useParams<{entityName: string}>();
  const viewer = useViewer();
  const isCurrentViewerProfilePage =
    viewer?.username && profilePageEntity === viewer.username;

  const defaultTeam =
    enableAccountSelector && isCurrentViewerProfilePage
      ? accountTeams?.edges?.[0]?.node?.name
      : entityName;

  const listItems = data
    ? config.extractListItems(data)
    : Array(config.listSize).fill({}); // fill the skeleton loader

  const numListItems = !called && loading ? config.listSize : listItems.length;
  const pageInfo = config.extractPageInfo(data);

  const personalEntityName =
    config.extractPersonalEntity && data
      ? config.extractPersonalEntity(data)
      : undefined;

  // fetch the default values on mount
  React.useEffect(() => {
    if (!called) {
      fetchMore({
        variables: getQueryVars(),
      });
    }
  }, [called, fetchMore]);

  const instantFetch = (partialArgs: Partial<PaginationArgs> = {}) => {
    const args = getQueryVars();
    fetchMore({
      variables: {
        ...args,
        ...partialArgs,
      },
    });
  };

  /**
   * Debouncing fetchMore triggered by search pattern changes
   */
  // eslint-disable-next-line
  const debouncedFetch = React.useCallback(
    _.debounce((paginationArgs: PaginationArgs) => {
      fetchMore({
        variables: paginationArgs,
      });
    }, 350),
    [fetchMore]
  );

  // https://weightsandbiases.slack.com/archives/CR1B10HFW/p1694047085761879
  const fetchByDirection = (dir: 'next' | 'prev' | 'refetch') => {
    const curVars = getQueryVars();

    // Set the cursor back one page and fetch to refresh the list.
    if (dir === 'refetch') {
      try {
        const newCursor = offsetCursor(curVars.after, -1 * config.listSize);
        instantFetch({after: newCursor});
      } catch (e) {
        console.warn('Error refetching projects, going to the beginning');
        instantFetch({after: ''});
      }
      return;
    }

    const isForwardFetch = dir === 'next';

    if (isForwardFetch) {
      // pagination values work as expected with forward fetching since the first, after conditions are
      // translated correctly in the API layer
      instantFetch();
    } else {
      // backward fetching is more complicated, `endCursor` decoded from base64 looks like `arrayconnection:NN`
      // where NN is a number that represents the index of the last item. E.g. fetching the first 20 items will
      // return `arrayconnection:19` as the last number
      // moving backwards requires going back 2x the page size, e.g. a page fetch getting items 21-40 will
      // end with `arrayconnection:39`, so return the first 20 results
      try {
        const newCursor = offsetCursor(curVars.after, -2 * config.listSize);
        instantFetch({after: newCursor});
      } catch (e) {
        // handle anything unexpected by going back to the beginning
        console.error(e);
        instantFetch({after: ''});
      }
    }
  };

  // when the query is running the pagination info returned by the API will be undefined
  // we want to avoid updating queryVars and thrashing during that period
  React.useEffect(() => {
    if (!loading && pageInfo) {
      queryVars.current = {
        ...queryVars.current,
        entityName: entityName ?? '',
        userName: entityName ?? '',
        first: config.listSize,
        pattern: searchPattern,
        after: pageInfo?.endCursor ?? '',
        orgID: orgID ?? '',
        userID: userID ?? '',
        isReadOnly,
      };
    }
  }, [
    searchPattern,
    config.listSize,
    entityName,
    loading,
    pageInfo,
    orgID,
    userID,
    isReadOnly,
  ]);

  React.useEffect(() => {
    // whenever the sort order changes we want to start at the top of that ordered list
    queryVars.current = {
      ...queryVars.current,
      after: '',
      order: sortOrder[1] ? sortOrder.join('') : '',
    };
  }, [sortOrder]);

  return (
    <Tailwind>
      <Components.Wrapper>
        <Components.Meta
          listSize={!called && loading ? undefined : numListItems}
          fetchItems={fetchByDirection}
          hasNextPage={pageInfo?.hasNextPage ?? false}
          hasPreviousPage={pageInfo?.hasPreviousPage ?? false}
          searchPattern={searchPattern}
          setSearchPattern={(newPattern: string) => {
            setSearchPattern(newPattern);
            debouncedFetch({
              ...getQueryVars(),
              after: '',
              pattern: newPattern,
            });
          }}
        />
        <Components.ListHeader
          sortOrder={sortOrder}
          setSortOrder={(newSortOrder: [SortOperators, string]) => {
            setSortOrder(newSortOrder);
            instantFetch({
              after: '',
              order: newSortOrder.join(''),
            });
          }}
        />
        <Components.ListWrapper>
          {numListItems === 0 ? (
            <Components.EmptyState
              isSearching={getQueryVars().pattern !== ''}
              isCreatingProject={isCreatingProject}
              toggleCreateProjectDrawer={toggleCreateProjectDrawer}
              defaultTeam={defaultTeam ?? ''}
              isReadOnly={isReadOnly}
            />
          ) : (
            (listItems ?? []).map((item: ListItem, i: number) => (
              <Components.ListItem
                itemData={item}
                key={i}
                isLoading={loading}
                personalEntity={personalEntityName}
                onDeleted={() => {
                  fetchByDirection('refetch');
                }}
              />
            ))
          )}
        </Components.ListWrapper>
      </Components.Wrapper>
    </Tailwind>
  );
};

export const makePaginatedList =
  <QueryData, ListItem>(
    Components: PaginatedListProps<QueryData, ListItem>['Components'],
    config: PaginatedListProps<QueryData, ListItem>['config']
  ) =>
  (
    props: Omit<
      PaginatedListProps<QueryData, ListItem>,
      'Components' | 'config'
    >
  ) =>
    (
      <PaginatedList<QueryData, ListItem>
        Components={Components}
        config={config}
        {...props}
      />
    );
